﻿import { DefaultParameterDefinitions } from "../../DefaultParameterDefinitions";
import { InfluxDataWrapper } from "../../InfluxDb/InfluxDataWrapper";
import { MainSite } from "../../MainSite";
import { ParameterSets } from "../../Model/ClientConf/UserClientConf";
import { ParameterDefinition } from "../../Model/ParameterDefinition";
import { ChartCustomLimitsDialog } from "./ChartCustomLimitsDialog";
import { PlottingPage } from "./PlottingPage";

export class Chart {

    public containerDiv: JQuery;
    public div: JQuery;

    constructor(private mainSite: MainSite, private plottingPage: PlottingPage) {
        this.containerDiv = $('<div id="midi_PlottingPage_plot_scrolling_container"></div>');
        this.div = $('<div id="midi_PlottingPage_plot_container"></div>').appendTo(this.containerDiv);
    }

    public chart: __Highcharts.ChartObject;

    private seriesOptions: any[];
    private axisOptions: any[];

    private static applyDataFunction(data: InfluxDataWrapper, dataFunction: string):

        InfluxDataWrapper {

        // If ParameterDefinition was not provided, find the defaultDefinitions based on the ParameterId
        if (!data.ParameterDefinition) {
            
            let defaultDefinitions: ParameterDefinition | null = null;

            if (data.ParameterId in DefaultParameterDefinitions.Parameters) {

                defaultDefinitions = DefaultParameterDefinitions.Parameters[data.ParameterId];

            } else {
                // check for different versions of the Id (necessary due to inconsistent ParameterId nomenclature)

                const ParameterId = data.ParameterId.replace("_", "/");

                if (ParameterId in DefaultParameterDefinitions.Parameters) {

                    defaultDefinitions = DefaultParameterDefinitions.Parameters[ParameterId];

                } else {

                    const ParameterId = data.ParameterId.replace("/", "_");

                    if (ParameterId in DefaultParameterDefinitions.Parameters) {

                        defaultDefinitions = DefaultParameterDefinitions.Parameters[ParameterId];

                    }
                }
            }

                if (!defaultDefinitions) {
                    // Direction parameters come in many forms e.g. due to varying lidar bin ranges.
                    // Hence, this hack to make sure all directions are plotted as points.
                    if (data.ParameterId.toLowerCase().includes("dir") && data.ParameterId.toLowerCase().includes("deg")) {
                        defaultDefinitions = {
                            PlotLine: false,
                            Default: false
                        }
                    }
                }

                data.ParameterDefinition = defaultDefinitions;
            }

        switch (dataFunction) {

            case "roc":
                let r: InfluxDataWrapper = {
                    ...data,
                    ParameterDefinition:
                    {
                        ...data.ParameterDefinition,
                        AxisMin: null,
                        AxisMax: null,
                        Units: data.ParameterDefinition.Units !== null ? data.ParameterDefinition.Units + " / hour" : null,
                        AxisLabel: data.ParameterDefinition.AxisLabel !== null ? "RoC " + data.ParameterDefinition.AxisLabel : null
                    }
                };
                r.Data = [];
                let prev = null;
                for (let x of data.Data) {
                    if (x[1] === null) {
                        continue;
                    }
                    if (prev !== null) {
                        // TODO: It would be better to identify directional parameters more
                        // precisely
                        let dir = data.ParameterDefinition !== null &&
                            data.ParameterDefinition.AxisMin === 0 &&
                            data.ParameterDefinition.AxisMax === 360;

                        let dthrs = (x[0] - prev[0]) / (60 * 60000);
                        let dv = x[1] - prev[1];
                        if (dir) {
                            if (dv > 180) {
                                dv -= 360;
                            } else if (dv < -180) {
                                dv += 360;
                            }
                        }

                        r.Data.push([Math.floor((x[0] + prev[0]) / 2), dv / dthrs]);
                    }
                    prev = x;
                }
                return r;
            default:
                return data;
        }
    }

    // Draws the plot, and populates data based on content of dataToPlot
    public drawPlot(
        dataToPlot: InfluxDataWrapper[],
        stacked: boolean,
        from: moment.Moment,
        to: moment.Moment,
        groupingApproximation: string,
        dataFunction: string) {

        var timeZoneString = this.mainSite.getTimeZoneString();

        var usedAxisIds = [];

        this.seriesOptions = [];
        this.axisOptions = [];

        if (stacked) {
            var requiredHeight = dataToPlot.length * 300;
            var deficit = requiredHeight - this.div.height();

            var newBottom = Math.min(5, parseFloat(this.div.css('bottom')) - deficit);

            this.div.css('bottom', newBottom + 'px');
        } else {
            this.div.css('bottom', '5px');
        }

        dataToPlot.forEach((data: InfluxDataWrapper) => {
            if (data.Data === null) {
                alert("Error getting data.");
            } else {
                data.Data.unshift([from.valueOf(), null]);
                data.Data.push([to.valueOf(), null]);
            }

            data = Chart.applyDataFunction(data, dataFunction);

            if (stacked) {
                this.addSeriesToStackedChart(data, dataToPlot, usedAxisIds);
            } else {
                this.addSeriesToChart(data, usedAxisIds);
            }
        });

        var timeZone = this.mainSite.selectedRegion.TimeZone;

        let _Chart = this;
        this.div.highcharts('StockChart', <any>{ 
            chart: {
                zoomType: 'x'
            },
            credits: {
                enabled: false
            },
            exporting: {
                enabled: false,
                scale: 1,
                sourceWidth: 1024,
                sourceHeight: (stacked) ? Math.max(requiredHeight, 768) : 768
            },
            legend: {
                enabled: true
            },
            rangeSelector: {
                enabled: false,
                selected: 1
            },
            plotOptions: {
                series: {
                    events: {
                        legendItemClick: function () {
                            return false;
                        }
                    }
                },
                line: {
                    animation: false,
                    dataGrouping: {
                        approximation: groupingApproximation
                    }
                }
            },
            xAxis: {
                title: {
                    text: timeZoneString
                },
                ordinal: false,
                events: {
                    afterSetExtremes: (e: any) => {
                        this.afterSetExtremes(e);
                    }
                }
            },
            navigator: {
                adaptToUpdatedData: false,
                series: {
                    data: [[1425168000000, null], [moment.utc().endOf('M').valueOf(), null]]
                }
            },
            scrollbar: {
                liveRedraw: false,
                enabled: true
            },
            tooltip: {
                xDateFormat: '%Y-%m-%d %H:%M:%S ' + timeZoneString,
                valueDecimals: 2
            },
            yAxis: this.axisOptions,
            series: this.seriesOptions
        }, function (_highchart) {
            this.yAxis.forEach((axis, axisIndex) => {
                let _currentAxisId = usedAxisIds[axisIndex];
                _Chart.applyAxisFeatures(axis, axisIndex, _currentAxisId);
            });
        });

        this.chart = this.div.highcharts();

        if (to.valueOf() < from.valueOf()) {
            this.chart.showLoading('From date must be earlier than To date');
            return;
        }
    }

    public exportChart() {
        const labelFontSize = "14px";
        const titleFontSize = "16px";

        var exportingChartOptions: __Highstock.Options = <any>{
            navigator: {
                enabled: false
            },/*
            title: {
                text: "Shell " + branding.siteName + " Chart",
                style: {
                    "font-size": titleFontSize
                }
            },*/
            subtitle: {
                text: "Downloaded by " + this.plottingPage.mainSite.currentUser.Id + " on " + new Date(moment.utc().valueOf() + moment.utc().toDate().getTimezoneOffset() * 60000).toDateString() + " UTC",
                align: "right",
                style: {
                    "font-size": labelFontSize
                }
            }
        };

        let customLimitsSubstr = " <span style=\"color: blue; text-decoration: underline;\">Custom Limits</span>";

        //retain existing axis options so as not to override any formatting (e.g. datetime)
        (exportingChartOptions.xAxis as __Highcharts.AxisOptions | __Highcharts.AxisOptions[]) = JSON.parse(JSON.stringify(this.chart.options.xAxis));

        console.log(JSON.stringify(exportingChartOptions));
        if (Array.isArray(exportingChartOptions.xAxis)) {
            exportingChartOptions.xAxis.forEach((axis) => {
                console.log(JSON.stringify(axis));
                axis.labels["rotation"] = -85;
                axis.labels.style = axis.labels.style || {};
                axis.labels.style.fontSize = labelFontSize;
                axis.title.style = axis.title.style || {};
                axis.title.style.fontSize = titleFontSize;
                if (axis.title.text.indexOf(customLimitsSubstr) !== -1) {
                    axis.title.text = axis.title.text.substring(0, axis.title.text.indexOf(customLimitsSubstr));
                }
            });
        }
        else {
            exportingChartOptions.xAxis.labels["rotation"] = -85;
            exportingChartOptions.xAxis.labels.style = exportingChartOptions.xAxis.labels.style || {};
            exportingChartOptions.xAxis.labels["style"]["font-size"] = labelFontSize;
            exportingChartOptions.xAxis.title.style = exportingChartOptions.xAxis.title.style || {};
            exportingChartOptions.xAxis.title.style.fontSize = titleFontSize;
            if (exportingChartOptions.xAxis.title.text.indexOf(customLimitsSubstr) !== -1) {
                exportingChartOptions.xAxis.title.text = exportingChartOptions.xAxis.title.text.substring(0, exportingChartOptions.xAxis.title.text.indexOf(customLimitsSubstr));
            }
        }

        //retain existing axis options so as not to override any formatting (e.g. datetime)
        (exportingChartOptions.yAxis as __Highcharts.AxisOptions | __Highcharts.AxisOptions[]) = JSON.parse(JSON.stringify(this.chart.options.yAxis));      

        if (Array.isArray(exportingChartOptions.yAxis)) {
            exportingChartOptions.yAxis.forEach((axis) => {
                console.log(JSON.stringify(axis));
                axis.labels.style = axis.labels.style || {};
                axis.labels.style.fontSize = labelFontSize;
                axis.title.style = axis.title.style || {};
                axis.title.style.fontSize = titleFontSize;
                if (axis.title.text.indexOf(customLimitsSubstr) !== -1) {
                    axis.title.text = axis.title.text.substring(0, axis.title.text.indexOf(customLimitsSubstr));
                }
            });
        }
        else {
            exportingChartOptions.yAxis.labels.style = exportingChartOptions.yAxis.labels.style || {};
            exportingChartOptions.yAxis.labels["style"]["font-size"] = labelFontSize;
            exportingChartOptions.yAxis.title.style = exportingChartOptions.yAxis.title.style || {};
            exportingChartOptions.yAxis.title.style.fontSize = titleFontSize;
            if (exportingChartOptions.yAxis.title.text.indexOf(customLimitsSubstr) !== -1) {
                exportingChartOptions.yAxis.title.text = exportingChartOptions.yAxis.title.text.substring(0, exportingChartOptions.yAxis.title.text.indexOf(customLimitsSubstr));
            }
        }

        var exportingOptions: __Highcharts.ExportingOptions = {
            filename: "Chart",
            width: 1024
        };

        this.chart.exportChart(exportingOptions, exportingChartOptions);
    }

    private afterSetExtremes(e: any) {
        //var fromMom = moment.utc(e.min).startOf('minute');
        //var toMom = moment.utc(e.max).startOf('minute');
        var fromMomTz = moment.utc(e.min).tz(this.mainSite.getTimeZoneName()).startOf('minute');
        var toMomTz = moment.utc(e.max).tz(this.mainSite.getTimeZoneName()).startOf('minute');

        if (this.plottingPage._debug) {
            console.info(`%c[PLOT CHART]`, 'color: orange; font-weight: bold; font-style: italic;', ` afterSetExtremes(${e})... THEN plottingPage.setDateRange()`, { e: e, fromMomTz: fromMomTz, toMomTz: toMomTz });
        }

        this.plottingPage.setDateRange(fromMomTz, toMomTz, true);
    }

    public getSeriesNameOrId(data: InfluxDataWrapper, isStacked: boolean = false, type: string = "name"): string {

        if (type == "id") {
            return Chart.getYAxisId(data);
        }
        if (type == "name") {
            let paramLabel: string = data.ParameterId;
            if (data.ParameterDefinition) {
                ///paramLabel = data.ParameterDefinition.DisplayName ?? (data.ParameterDefinition.AxisLabel ?? data.ParameterId);
                paramLabel = data.ParameterDefinition.DisplayName ?? data.ParameterId;
                /*if (data.ParameterDefinition.Suffix) {
                    paramLabel += ` (${data.ParameterDefinition.Suffix})`;
                }*/

                if (data.ParameterDefinition.Units) {
                    let convertedUnits = MainSite.getUnitConverter().getConvertedUnits(data.ParameterDefinition.Units, data.ParameterId);
                    if (convertedUnits !== data.ParameterDefinition.Units) {
                        paramLabel += ' (' + MainSite.getUnitConverter().getConvertedUnits(data.ParameterDefinition.Units, data.ParameterId) + ')';
                    }
                }
            }

            let sensorLabel = MainSite.instance.staticDataManager.Schema?.ParameterSchema
                ?.Stations?.[data.StationId]?.Sensors?.[data.SensorId]?.SensorDefinition?.DisplayName
            if (!sensorLabel || sensorLabel.indexOf("/") !== -1) {
                sensorLabel = data.SensorId;
            }

            const stationLabel = MainSite.instance.staticDataManager.Schema.ParameterSchema.Stations[data.StationId].Station.Label

            if (isStacked) {
                return stationLabel + "." + sensorLabel + "." + paramLabel;
            } else {
                let variantName = data.VariantId == undefined ? "" : " (" + data.VariantId + ")";
                return stationLabel + "." + sensorLabel + "." + paramLabel + variantName;
            }
        }
    }

    public static getYAxisId(data: InfluxDataWrapper): string {
        /** Parameter ID as provided ("raw"). This is then modified to attempt grouping of parameters where they should share an axis (e.g. Total_CurrentDirection_BinXX_XXm_deg). */
        let paramId: string = data.ParameterId;

        if (data.ParameterDefinition) {
            paramId = data.ParameterDefinition.AxisLabel;
            if (data.ParameterDefinition.Units) {
                paramId += ' (' + MainSite.getUnitConverter().getConvertedUnits(data.ParameterDefinition.Units, data.ParameterId) + ')';
            }
        } else {
            //: try grouping currents/wind profile elements with bins & depths: (addresses User Story #857)
            var pattern = /(.*)_Bin(?:\d+)_(?:(?:\d+|\d+\.*\d+))m_(.*)/i; //  /(.*)_Bin(?:\d+)_(?:\d+)m_(.*)/i;
            if (pattern.test(paramId)) { //: assumes params such as "Total_CurrentDirection_Bin03_7m_deg_Mean" for NorthSea.TetraSpar for example
                let m = pattern.exec(paramId);
                paramId = m[1] + '_' + m[2];
            }

            //: Group param IDs containing '_10min', '_30min' variations, or '_Mean' suffix, or sensor height variations ('_10m_' etc).. These should really share same axes as well, as the paramter itself and axis range should be the same:
            paramId = paramId.replace("_Min", "");
            paramId = paramId.replace("_Max", "");
            paramId = paramId.replace("_Mean", "");
            paramId = paramId.replace(/_\d+min/, ""); //.replace("_10min", "");
            paramId = paramId.replace(/_\d+m$/, ""); //: sensor height variations (assumes it's at the end of the modified param id)
        }

        return paramId;
    }

    private addSeriesToChart(data: InfluxDataWrapper, usedAxisIds: string[]) {
        /**
        * Adds the series and axis to the chart. Chart does not need to be re-drawn.
        * Only adds an axis if required.
        */
        this.chart.hideLoading();

        var axisId = Chart.getYAxisId(data);

        if (usedAxisIds.indexOf(axisId) < 0) {
            let _custom_conf = this.getCustomConf(axisId);
            let _min = (data.ParameterDefinition && typeof data.ParameterDefinition.AxisMin !== "undefined") ? data.ParameterDefinition.AxisMin : null;
            let _max = (data.ParameterDefinition && typeof data.ParameterDefinition.AxisMax !== "undefined") ? data.ParameterDefinition.AxisMax : null;

            if (typeof (_custom_conf) == "object" && _custom_conf !== null && "min" in _custom_conf && !isNaN(_custom_conf.min) && _custom_conf.min !== null) {
                _min = _custom_conf.min;
            }
            if (typeof (_custom_conf) == "object" && _custom_conf !== null && "max" in _custom_conf && !isNaN(_custom_conf.max) && _custom_conf.max !== null) {
                _max = _custom_conf.max;
            }

            this.axisOptions.push({
                title: {
                    text: axisId + " <span style=\"color: blue; text-decoration: underline;\">Custom Limits</span>",
                    useHtml: true
                },
                opposite: usedAxisIds.length % 2 == 1,
                min: _min,
                max: _max
            });
            usedAxisIds.push(axisId);
        }

        var variantName = data.VariantId == undefined ? "" : " (" + data.VariantId + ")";

        var options: any = {
            name: this.getSeriesNameOrId(data, false, "name"), // stationLabel + "." + data.SensorId + "." + data.ParameterId + variantName,
            id: this.getSeriesNameOrId(data, false, "id"), // stationLabel + "." + data.SensorId + "." + data.ParameterId,
            yAxis: usedAxisIds.indexOf(axisId) + 0, // + 2 when using addSeries and navigator enabled
            data: data.Data,
            lineWidth: (() => {
                if (data.ParameterDefinition && !data.ParameterDefinition.PlotLine) {
                    return 0;
                }
                return 1;
            })(),
            marker: {
                enabled: data.ParameterDefinition && !data.ParameterDefinition.PlotLine,
                radius: 3
            }
        };

        if (data.SensorId.indexOf("Forecast") == -1) {
            options.gapSize = 7200000;// 2 hours in miliseconds
            options.gapUnit = "value";
        }

        this.seriesOptions.push(options);
    }

    private addSeriesToStackedChart(data: InfluxDataWrapper, dataToPlot: InfluxDataWrapper[], usedAxisIds: string[]) {
        /**
        * Adds the series and axis to the chart. Chart needs to be re-drawn.
        * Axes are always added.
        * Need to account for the possibility of the ParameterDefinition not being set.
        */

        this.chart.hideLoading();

        var height = String(100 / dataToPlot.length - (15 / dataToPlot.length)) + "%";
        var top = String(100 / dataToPlot.length * usedAxisIds.length) + "%";

        var axisId = Chart.getYAxisId(data);

        // let _loadedParamSetName = this.plottingPage.parameterSetList.getLoadedParamSetName();
        let _loadedParamSetValues = this.plottingPage.parameterSetList.getLoadedParamSetValues();

        let _custom_conf = this.getCustomConf(axisId);
        let _min = (data.ParameterDefinition && typeof data.ParameterDefinition.AxisMin !== "undefined") ? data.ParameterDefinition.AxisMin : null;
        let _max = (data.ParameterDefinition && typeof data.ParameterDefinition.AxisMax !== "undefined") ? data.ParameterDefinition.AxisMax : null;

        if (typeof (_custom_conf) == "object" && _custom_conf !== null && "min" in _custom_conf && !isNaN(_custom_conf.min) && _custom_conf.min !== null) {
            _min = _custom_conf.min;
        }
        if (typeof (_custom_conf) == "object" && _custom_conf !== null && "max" in _custom_conf && !isNaN(_custom_conf.max) && _custom_conf.max !== null) {
            _max = _custom_conf.max;
        }

        //this.chart.addAxis({
        this.axisOptions.push({
            title: {
                text: axisId + " <span style=\"color: blue; text-decoration: underline;\">Custom Limits</span>",
                useHtml: true
            },
            height: height,
            top: top,
            offset: 0,
            opposite: false,
            min: _min,
            max: _max
        });
        usedAxisIds.push(axisId);

        //this.chart.addSeries({

        this.seriesOptions.push({
            name: this.getSeriesNameOrId(data, true, "name"), // data.StationId + "." + data.SensorId + "." + data.ParameterId,
            id: this.getSeriesNameOrId(data, true, "id"), // data.StationId + "." + data.SensorId + "." + data.ParameterId,
            yAxis: usedAxisIds.length - 1, // + 1 when using addSeries!
            data: data.Data,
            lineWidth: (() => {
                if (data.ParameterDefinition && !data.ParameterDefinition.PlotLine) {
                    return 0;
                }
                return 1;
            })(),
            marker: {
                enabled: data.ParameterDefinition && !data.ParameterDefinition.PlotLine,
                radius: 3
            }
        });
    }

    public applyAxisFeatures(axis: __Highcharts.AxisObject|any, axisIndex: number, _currentAxisId: string) {
        // console.info("AxisFeats", { axis: axis, axisIndex: axisIndex, currentAxisId: _currentAxisId });
        let _linkNode = axis.axisGroup.element;
        try {
            if (typeof axis.axisTitle !== "undefined" && typeof axis.axisTitle.element.children[1] !== "undefined") {
                _linkNode = axis.axisTitle.element.children[1];
            }
        } catch (err) {
            console.warn("AxisFeatures Error", { err: err, axis: axis, axisIndex: axisIndex, currentAxisId: _currentAxisId });
        }
        _linkNode.style.cursor = "pointer";

        let _Chart = this;
        _linkNode.onclick = function () {
            let _custom_conf = _Chart.getCustomConf(_currentAxisId);

            // console.info("axis click", { this: this, axis: axis, axisIndex: axisIndex, _Chart: _Chart, currentAxisId: _currentAxisId, _custom_conf: _custom_conf });
            new ChartCustomLimitsDialog(this.mainSite, _Chart.plottingPage, _Chart, axis, axisIndex, _currentAxisId, _custom_conf).show();
        };
    }

    public getCustomConf(axisId: string): ParameterSets.PlotAxisConfig {
        let _loadedParamSetValues = this.plottingPage.parameterSetList.getLoadedParamSetValues();
        // console.info("Chart.getCustomConf()", { axisId: axisId, loadedParamSetVals: _loadedParamSetValues });

        if (_loadedParamSetValues && _loadedParamSetValues.plotConfigurations && axisId in _loadedParamSetValues.plotConfigurations) {
            return _loadedParamSetValues.plotConfigurations[axisId];
        } else {
            if (this.plottingPage.customLimitsByAxisLabel && axisId in this.plottingPage.customLimitsByAxisLabel) {
                return this.plottingPage.customLimitsByAxisLabel[axisId];
            }

            return null;
        }
    }
}

 